import type { Citation } from '../types';
import { onCitationClick, type PlaybackTarget } from '../audio/player';

interface Props {
  answer: string;
  citations: Citation[];
}

/**
 * Render the synthesised answer, replacing [N] markers with clickable anchors
 * that drive the audio player to that timestamp. This is the most-used
 * interaction in the app — every claim the synthesiser makes should be one
 * click away from "play me the actual audio".
 */
export function Synthesis({ answer, citations }: Props) {
  const parts = answer.split(/(\[\d+\])/g);
  return (
    <article className="synthesis">
      {parts.map((part, i) => {
        const m = /^\[(\d+)\]$/.exec(part);
        if (!m) return <span key={i}>{part}</span>;
        const idx = parseInt(m[1], 10);
        const cit = citations[idx - 1];
        if (!cit) return <span key={i} className="citation-missing">{part}</span>;
        const target: PlaybackTarget = {
          episode_id: cit.episode_id,
          start_sec: cit.start_sec,
          end_sec: cit.end_sec,
          label: `${cit.speaker ?? 'Unknown speaker'} — ${formatTimestamp(cit.start_sec)}`,
        };
        return (
          <button
            key={i}
            className="citation"
            title={cit.excerpt.slice(0, 200)}
            onClick={() => onCitationClick(target)}
          >
            [{idx}]
          </button>
        );
      })}
    </article>
  );
}

function formatTimestamp(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
